import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { mandalaService } from '../../services/mandalaService';
import { MandalaChart } from '../../types/Mandala';
import PageHeader from '../common/PageHeader';
import './MandalaManagement.css';

interface MandalaManagementProps {
  onToggleSidebar: () => void;
}

const MandalaManagement: React.FC<MandalaManagementProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mandalas, setMandalas] = useState<MandalaChart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMandalaTitle, setNewMandalaTitle] = useState('');
  const [newMandalaDescription, setNewMandalaDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMandalas, setFilteredMandalas] = useState<MandalaChart[]>([]);

  useEffect(() => {
    if (user) {
      loadMandalas();
    }
  }, [user]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredMandalas(mandalas);
    } else {
      const filtered = mandalas.filter(mandala =>
        mandala.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (mandala.description && mandala.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredMandalas(filtered);
    }
  }, [mandalas, searchQuery]);

  const loadMandalas = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const response = await mandalaService.getUserMandalaCharts({
        user_id: user.id,
        limit: 100,
        offset: 0
      });
      if (response.success && response.charts) {
        setMandalas(response.charts);
      }
    } catch (err) {
      console.error('Error loading mandala charts:', err);
      setError('Failed to load mandala charts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMandala = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newMandalaTitle.trim()) return;

    try {
      setError(null);
      const response = await mandalaService.createMandalaChart({
        user_id: user.id,
        title: newMandalaTitle.trim(),
        description: newMandalaDescription.trim() || undefined
      });
      if (response.success && response.id) {
        await loadMandalas();
        setNewMandalaTitle('');
        setNewMandalaDescription('');
        setShowCreateForm(false);
      } else {
        setError(response.message || 'Failed to create mandala chart');
      }
    } catch (err) {
      console.error('Error creating mandala chart:', err);
      setError('Failed to create mandala chart');
    }
  };

  const handleDeleteMandala = async (mandalaId: number) => {
    if (!window.confirm('Are you sure you want to delete this mandala chart?')) return;

    try {
      setError(null);
      const response = await mandalaService.deleteMandalaChart(mandalaId.toString());
      if (response.success) {
        setMandalas(mandalas.filter(mandala => mandala.id !== mandalaId));
      } else {
        setError(response.message || 'Failed to delete mandala chart');
      }
    } catch (err) {
      console.error('Error deleting mandala chart:', err);
      setError('Failed to delete mandala chart');
    }
  };

  const handleMandalaClick = (mandalaId: number) => {
    navigate(`/mandala/${mandalaId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="mandala-management">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/mandala</h1>}
        rightActions={
          <button 
            className="mandala-create-btn"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '✕' : '+'} {showCreateForm ? 'Cancel' : 'New Mandala'}
          </button>
        }
      />
      
      <div className="mandala-management-content">
        {error && (
          <div className="error-message">{error}</div>
        )}

        {showCreateForm && (
          <div className="create-mandala-form">
            <h3>Create New Mandala Chart</h3>
            <form onSubmit={handleCreateMandala}>
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  id="title"
                  type="text"
                  value={newMandalaTitle}
                  onChange={(e) => setNewMandalaTitle(e.target.value)}
                  placeholder="Enter mandala chart title"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={newMandalaDescription}
                  onChange={(e) => setNewMandalaDescription(e.target.value)}
                  placeholder="Enter description (optional)"
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Create</button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewMandalaTitle('');
                    setNewMandalaDescription('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="mandala-search">
          <input
            type="text"
            placeholder="Search mandala charts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {loading ? (
          <div className="loading">Loading mandala charts...</div>
        ) : filteredMandalas.length === 0 ? (
          <div className="empty-state">
            <p>No mandala charts found.</p>
            {!showCreateForm && (
              <button 
                className="btn-primary"
                onClick={() => setShowCreateForm(true)}
              >
                Create Your First Mandala Chart
              </button>
            )}
          </div>
        ) : (
          <div className="mandala-list">
            {filteredMandalas.map((mandala) => (
              <div 
                key={mandala.id} 
                className="mandala-card"
                onClick={() => handleMandalaClick(mandala.id)}
              >
                <div className="mandala-card-header">
                  <h3>{mandala.title}</h3>
                  <button
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMandala(mandala.id);
                    }}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
                {mandala.description && (
                  <p className="mandala-description">{mandala.description}</p>
                )}
                <div className="mandala-meta">
                  <span className="mandala-date">
                    Created: {formatDate(mandala.created_at)}
                  </span>
                  {mandala.cells && (
                    <span className="mandala-cells">
                      {mandala.cells.length} cells
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MandalaManagement;

